import React from "react";
import { Box, Typography } from "@mui/material";

// ═════════════════════════════════════════════════════════════════════
//  PageLoader — the "Loading Your Data…" screen shown instead of a blank page while a page's data loads:
//  gold ring spinning round the MarketSync logo + the title. Nothing else.
//
//    <PageLoader />                        fills the page area (min-height 70vh)
//    <PageLoader fullScreen />             whole window (route / lazy-page loading)
//    <PageLoader compact />                smaller, for a panel or a table area
// ═════════════════════════════════════════════════════════════════════

const GOLD = "#c99a35";
const GOLD_DARK = "#8c6a1c";
const CSS = `
@keyframes plSpin { to { transform: rotate(360deg); } }
@keyframes plSpinRev { to { transform: rotate(-360deg); } }
@keyframes plPulse { 0%,100% { transform: scale(1); opacity: .55; } 50% { transform: scale(1.06); opacity: 1; } }
@keyframes plFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
@keyframes plFadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
`;

export default function PageLoader({ fullScreen = false, compact = false, title = "Loading", highlight = "Your Data..." }) {
  const size = compact ? 92 : 124;
  const disc = compact ? 62 : 84;
  const logo = compact ? 28 : 38;

  return (
    <Box role="status" aria-live="polite" sx={{
      width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      minHeight: fullScreen ? "100vh" : compact ? 240 : "60vh",
      py: compact ? 4 : 6, px: 2, textAlign: "center", boxSizing: "border-box",
      background: fullScreen
        ? "radial-gradient(ellipse at 50% 30%, #fffaf0 0%, #fbf4e4 55%, #f6ecd4 100%)"
        : "transparent",
    }}>
      <style>{CSS}</style>

      {/* ring + logo */}
      <Box sx={{ position: "relative", width: size, height: size, mb: compact ? 2 : 3, animation: "plFadeUp .5s ease both" }}>
        <Box sx={{ position: "absolute", inset: -size * 0.12, borderRadius: "50%", border: "1px solid rgba(201,154,53,0.25)", animation: "plPulse 2.6s ease-in-out infinite" }} />
        <Box sx={{ position: "absolute", inset: -size * 0.28, borderRadius: "50%", border: "1px solid rgba(201,154,53,0.12)" }} />
        {/* slow orbit with two dots */}
        <Box sx={{ position: "absolute", inset: -size * 0.12, animation: "plSpinRev 9s linear infinite" }}>
          <Box sx={{ position: "absolute", top: "4%", left: "22%", width: compact ? 6 : 10, height: compact ? 6 : 10, borderRadius: "50%", bgcolor: GOLD }} />
          <Box sx={{ position: "absolute", bottom: "10%", right: "-1%", width: compact ? 5 : 8, height: compact ? 5 : 8, borderRadius: "50%", bgcolor: "#e0b84e" }} />
        </Box>
        {/* spinning gold arc */}
        <Box component="svg" viewBox="0 0 100 100" sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", animation: "plSpin 1.6s linear infinite" }}>
          <defs>
            <linearGradient id="plArc" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#f3d27a" />
              <stop offset="100%" stopColor={GOLD_DARK} />
            </linearGradient>
          </defs>
          <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(201,154,53,0.14)" strokeWidth="5" />
          <circle cx="50" cy="50" r="44" fill="none" stroke="url(#plArc)" strokeWidth="5" strokeLinecap="round" strokeDasharray="150 277" />
        </Box>
        {/* logo disc */}
        <Box sx={{
          position: "absolute", inset: 0, margin: "auto", width: disc, height: disc, borderRadius: "50%", // centred with margin:auto — a transform here would be overridden by the float animation and push the logo off-centre
          background: "radial-gradient(circle at 35% 30%, #ffffff, #fbf1d8)", boxShadow: "0 10px 30px rgba(184,137,44,0.28), inset 0 2px 6px rgba(255,255,255,0.9)",
          display: "flex", alignItems: "center", justifyContent: "center", animation: "plFloat 3s ease-in-out infinite",
        }}>
          <Box component="img" src="/assests/website-logo.png" alt="MarketSync" sx={{ width: logo, height: "auto", objectFit: "contain" }} />
        </Box>
      </Box>

      <Typography sx={{ fontSize: compact ? "1.15rem" : { xs: "1.3rem", sm: "1.6rem" }, fontWeight: 800, letterSpacing: -0.5, color: "#14161c", lineHeight: 1.15, animation: "plFadeUp .5s .1s ease both" }}>
        {title}{" "}
        <Box component="span" sx={{ background: "linear-gradient(90deg,#d4a73a,#a47711)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>{highlight}</Box>
      </Typography>
    </Box>
  );
}
